<template>
    <slot />
    <Teleport to="body">
        <pbl-dialog
            ref="dialogRef"
            :headline="
                commentId
                    ? t('common.conditionStatus.content.commentModal.titles.edit')
                    : t('common.conditionStatus.content.commentModal.titles.add')
            "
            click-shield
            class="comment-dialog"
        >
            <div class="comment-modal">
                <div class="comment-modal__selector">
                    <VeeFormSingleSelectField
                        name="reportNumber"
                        :label="t('common.conditionStatus.content.commentModal.labels.reportNumber')"
                        :placeholder="t('formInputs.common.placeholders.selectOption')"
                        :options="reportNumberOptions"
                        :disabled="reportNumberOptions.length === 0 || !!commentId"
                    />
                    <VeeFormSingleSelectField
                        name="analysisType"
                        :label="t('common.conditionStatus.content.commentModal.labels.analysisType')"
                        :placeholder="t('formInputs.common.placeholders.selectOption')"
                        :options="analysisTypeOptions"
                        :disabled="analysisTypeOptions.length === 0 || !!commentId"
                    />
                    <VeeFormSingleSelectField
                        name="measurement"
                        :label="t('common.conditionStatus.content.commentModal.labels.measurement')"
                        :placeholder="t('formInputs.common.placeholders.selectOption')"
                        :options="measurementOptions"
                        :disabled="measurementOptions.length === 0 || !!commentId"
                    />
                </div>
                <div class="comment-modal__textarea">
                    <VeeTextareaField
                        name="text"
                        :label="t('common.conditionStatus.content.commentModal.labels.text')"
                        :placeholder="t('formInputs.common.placeholders.enterValue')"
                    />
                </div>
            </div>
            <pbl-button
                slot="footer"
                variant="secondary"
                @click="closeModal"
            >
                {{ t('common.conditionStatus.content.commentModal.actions.cancel') }}
            </pbl-button>
            <pbl-button
                slot="footer"
                :disabled="!meta.dirty || isMutating"
                :loading="isMutating"
                @click="onSubmit"
            >
                {{ t('common.conditionStatus.content.commentModal.actions.confirm') }}
            </pbl-button>
        </pbl-dialog>
    </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import {
    ConditionStatusOpenCommentPayload,
    provideConditionStatusCommentModal,
} from '@/components/modals/comment/useConditionStatusCommentModal'
import { computed, ref } from 'vue'
import { PebbleDialog } from '@pebble/core'
import { useField, useForm } from 'vee-validate'
import { commentValidationSchema } from '@/components/modals/comment/ConditionStatusCommentModal.form'
import { useConditionStatusCommentSelectionHierarchy } from '@/components/modals/comment/useConditionStatusCommentSelectionHierarchy'
import { useCommentsAddMutation } from '@/api/mutations/comments/useCommentsAddMutation'
import { AxiosError } from 'axios'
import { useCommentsEditMutation } from '@/api/mutations/comments/useCommentsEditMutation'
import VeeTextareaField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeTextareaField.vue'
import VeeFormSingleSelectField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeFormSingleSelectField.vue'
import { ComponentType } from '@/configuration/component/component-types'
import { AddCommentRequest, EditCommentRequest } from '@/api/generated/HCA'
import { PARAMETER_UNIT_SEPARATOR } from '@/configuration/measurements/constants'

const { t } = useI18n()
const toasts = usePebbleToasts()

const props = defineProps<{
    componentId: string,
    componentType: ComponentType,
}>()

const dialogRef = ref<PebbleDialog | null>(null)
const dialogOpened = ref(false)

const { reportNumberOptions, createComputedAnalysisTypeOptions, createComputedMeasurementOptions } =
    useConditionStatusCommentSelectionHierarchy(props.componentId, props.componentType, dialogOpened)

const { handleSubmit, resetForm, meta } = useForm({
    validationSchema: commentValidationSchema,
    initialValues: {
        commentId: undefined,
        reportNumber: undefined,
        analysisType: undefined,
        measurement: undefined,
        text: '',
    },
})

const { value: commentId } = useField<number | undefined>(() => 'commentId')
const { value: reportNumberValue } = useField<string>(() => 'reportNumber')
const { value: analysisTypeValue } = useField<number | undefined>(() => 'analysisType')

const analysisTypeOptions = createComputedAnalysisTypeOptions(reportNumberValue)
const measurementOptions = createComputedMeasurementOptions(reportNumberValue, analysisTypeValue)

const { mutateAsync: mutateAddAsync, isPending: isAddPending } = useCommentsAddMutation()
const { mutateAsync: mutateEditAsync, isPending: isEditPending } = useCommentsEditMutation()

const isMutating = computed(() => isAddPending.value || isEditPending.value)

const openComment = (payload?: ConditionStatusOpenCommentPayload) => {
    resetForm({
        values: {
            commentId: payload?.comment?.id,
            reportNumber: payload?.comment?.reportNumber ?? undefined,
            analysisType: payload?.comment?.analysisType ?? undefined,
            measurement: payload?.comment?.unit && payload?.comment?.parameterId ? `${payload?.comment?.parameterId}${PARAMETER_UNIT_SEPARATOR}${payload?.comment?.unit}` : payload?.comment?.parameterId ?? undefined,
            text: payload?.comment?.text ?? '',
        },
    })

    dialogRef.value?.show()
    dialogOpened.value = true
}

const closeModal = () => {
    dialogRef.value?.hide()
    dialogOpened.value = false
}

const onSubmit = handleSubmit(async (values) => {

    try {
        if (values.commentId) {
            const commentData: EditCommentRequest = {
                commentId: values.commentId,
                data: {
                    text: values.text,
                }
            }
            await mutateEditAsync({
                commentData,
                componentSid: props.componentId,
                componentType: props.componentType,
                analysisType: values.analysisType,
            })
        } else {
            const commentData: AddCommentRequest = {
                componentSid: props.componentId,
                componentType: props.componentType,
                data: {
                    reportNumber: values.reportNumber,
                    analysisType: values.analysisType,
                    parameterId: values.measurement?.toString().includes(PARAMETER_UNIT_SEPARATOR) ? Number(values.measurement?.toString().split(PARAMETER_UNIT_SEPARATOR)[0]) : values.measurement as number | undefined,
                    unit: values.measurement?.toString().includes(PARAMETER_UNIT_SEPARATOR) ? values.measurement?.toString().split(PARAMETER_UNIT_SEPARATOR)[1] : undefined,
                    text: values.text,
                }
            }
            await mutateAddAsync({
                commentData,
            })
        }

        closeModal()
    } catch (err) {
        if (err instanceof AxiosError) {
            toasts?.error({
                message: t('common.errors.unknown'),
                duration: 3000,
            })
        }
    }
})

provideConditionStatusCommentModal({
    openComment,
})
</script>

<style scoped lang="scss">
.comment-dialog {
    --width: 70vw;
}

.comment-modal {
    display: flex;
    flex-direction: column;

    gap: $spacing-m;

    &__selector {
        display: flex;
        gap: $spacing-m;

        & > * {
            flex-grow: 1;
        }
    }
}
</style>
